import { useCallback, useEffect } from 'react';
import { MessageResponse } from '~/types';
import { validateEmail } from '~/utils';
import { useCommonChatStore } from '~/store';
import Chat from './Chat';
import * as S from './ChatContent.styled';

interface ChatContainerProps {
  type: 'group' | 'single';
  messages: MessageResponse[];
  waitingChat: boolean;
  isInfoMessageOn: boolean;
  infoMessageRef: React.RefObject<HTMLLIElement>;
  isPaginationPending: boolean;
}

export default function ChatContainer({
  type,
  messages,
  waitingChat,
  isInfoMessageOn,
  infoMessageRef,
  isPaginationPending,
}: ChatContainerProps) {
  const { activeEvaluation, setActiveEvaluation } = useCommonChatStore(
    (state) => ({
      activeEvaluation: state.activeEvaluation,
      setActiveEvaluation: state.setActiveEvaluation,
    }),
  );

  useEffect(() => {
    if (
      messages.length > 0 &&
      !validateEmail(messages[messages.length - 1].id)
    ) {
      setActiveEvaluation({
        id: messages[messages.length - 1].id,
        send_time: messages[messages.length - 1].send_time!,
      });
    }
  }, [messages.length]);

  const toggleEvaluation = (id: string, send_time: string) => {
    setActiveEvaluation((prev) =>
      prev && prev.id === id && prev.send_time === send_time
        ? null
        : { id, send_time },
    );
  };

  const groupMessages = useCallback((messages: MessageResponse[]) => {
    return messages.map((message, index) => ({
      ...message,
      isGrouped: index === 0 || messages[index - 1].id !== message.id,
    }));
  }, []);

  const groupedMessages = groupMessages(messages);

  return (
    <S.ChatBox>
      {isInfoMessageOn && (
        <>
          <S.SystemMessageBox>
            <S.ChatContentWrapper className='info'>
              <S.SystemMessage className='s'>
                Messages sent by the character are
                <br />
                all generated content.
              </S.SystemMessage>
            </S.ChatContentWrapper>
          </S.SystemMessageBox>
          {!isPaginationPending && <li ref={infoMessageRef} />}
        </>
      )}

      {groupedMessages.map(
        (
          { msg, id: responseEmail, send_time, name, isPending, isGrouped },
          idx,
        ) => {
          if (responseEmail === 'system_message') {
            const [name, ...rest] = msg.split(' ');
            const restMessage = rest.join(' ');

            return (
              <S.SystemMessageBox key={idx}>
                <S.ChatContentWrapper className='info'>
                  <S.SystemMessage>
                    <span>{name}</span>
                    {restMessage}
                  </S.SystemMessage>
                </S.ChatContentWrapper>
              </S.SystemMessageBox>
            );
          }

          if (send_time === 'error') {
            return (
              <S.SystemMessageBox key={idx}>
                <S.ChatContentWrapper className={'other'}>
                  <S.Content className='content'>
                    <p>{msg}</p>
                  </S.Content>
                </S.ChatContentWrapper>
              </S.SystemMessageBox>
            );
          }

          if (!send_time) return null;
          const isLastOrDifferentUser =
            idx === messages.length - 1 ||
            messages[idx + 1].id !== responseEmail;

          return (
            <Chat
              type={type}
              key={idx}
              msg={msg}
              id={responseEmail}
              send_time={send_time}
              name={name}
              isLastOrDifferentUser={isLastOrDifferentUser}
              isGrouped={isGrouped}
              showEvaluation={
                activeEvaluation?.id === responseEmail &&
                activeEvaluation?.send_time === send_time
              }
              toggleEvaluation={() =>
                toggleEvaluation(responseEmail, send_time)
              }
              isPending={isPending}
              messages={messages}
            />
          );
        },
      )}
      {waitingChat && (
        <S.DotLoaderBox>
          <S.DotLoader className='dot-falling' />
        </S.DotLoaderBox>
      )}
    </S.ChatBox>
  );
}
